/**
 * Health endpoint client.
 *
 * The first of the domain-split API modules. Kairos keeps every backend call in one
 * `api.ts`; here they are split by audience (`scan-`, `inspection-`, `fleet-`,
 * `defect-`, `admin-`) because one file serving five unrelated screens would have
 * five unrelated reasons to change.
 */

export interface HealthReport {
  readonly ok: boolean;
  readonly version: string;
  readonly database: 'up' | 'down';
}

export type HealthOutcome =
  | { readonly kind: 'reachable'; readonly report: HealthReport }
  | { readonly kind: 'unreachable'; readonly reason: string };

/**
 * Never throws. A dead API is an expected state for this app rather than an
 * exceptional one -- the whole product assumes the network is often absent -- so the
 * caller gets a value to render, not an exception to catch.
 */
export async function fetchHealth(): Promise<HealthOutcome> {
  try {
    const response = await fetch('/api/health');
    const report = (await response.json()) as HealthReport;
    return { kind: 'reachable', report };
  } catch (error) {
    return { kind: 'unreachable', reason: error instanceof Error ? error.message : 'unknown' };
  }
}
